import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from './ConfirmModal';

const ConfirmDialogContext = createContext();

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

export const ConfirmDialogProvider = ({ children }) => {
  const { t } = useTranslation('common');
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    type: 'danger',
    onConfirm: null,
    onCancel: null
  });

  const confirm = useCallback(({
    title,
    message,
    confirmText,
    cancelText,
    type = 'danger',
    onConfirm,
    onCancel
  }) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title: title ?? t('confirmDialog.defaultTitle'),
        message,
        confirmText: confirmText ?? t('confirmDialog.confirm'),
        cancelText: cancelText ?? t('confirmDialog.cancel'),
        type,
        onConfirm: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          if (onConfirm) onConfirm();
          resolve(true);
        },
        onCancel: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          if (onCancel) onCancel();
          resolve(false);
        }
      });
    });
  }, [t]);

  const closeConfirm = useCallback(() => {
    setConfirmState(prev => {
      if (prev.onCancel) prev.onCancel();
      return { ...prev, isOpen: false };
    });
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </ConfirmDialogContext.Provider>
  );
};

